import React, { FC, useCallback, useEffect, useState } from 'react'
import { Box, useMediaQuery } from '@mui/material'
import { CommentList } from '../components/Comment/CommentList'
import { Navigation } from '../components/Navigation/Navigation'
import { TopicList } from '../components/Topic/TopicList'
import { maxWidth } from '../utils/mediaQuery'
import { countUnprocessedItems } from '../utils/topic'
import { useNavigate, useParams } from 'react-router'
import { routes } from '../constants/routes'
import { useModerationState } from '../providers/ModerationStateProvider'
import { useTitle } from '../hooks/title'
import { useLogin } from '../providers/LoginProvider'
import { isOperator } from '../utils/user'
import { AreenaTopicModal } from '../components/AreenaTopicModal/AreenaTopicModal'
import { Topic } from '../models/topic'
import { RoleGuard } from '../components/RoleGuard/RoleGuard'
import { useTopicEmailSubscription } from '../hooks/topicEmailSubscription'

export const ModerationPage: FC = () => {
  const { topicId } = useParams()
  const { user } = useLogin()
  const {
    topicsAndComments: {
      topics,
      selectedTopic,
      topicFilter,
      setTopicFilter,
      comments,
      notices,
      actions,
      tab,
      setTab,
      loadTopic,
      loading,
      requestNextTopicId
    }
  } = useModerationState()
  const isMobile = useMediaQuery(maxWidth('lg'))
  const navigate = useNavigate()
  const { period, onSubscribeTopicEmails } = useTopicEmailSubscription(topicId!)
  const [isAreenaTopicModalOpen, setIsAreenaTopicModalOpen] = useState(false)
  const [editedAreenaTopic, setEditedAreenaTopic] = useState<Topic>()

  const unprocessedItems = countUnprocessedItems(topics)
  useTitle(`${unprocessedItems > 0 ? `(${unprocessedItems}) ` : ''}Yle Comments Admin`)

  useEffect(() => {
    if (topicId && topicId !== selectedTopic?.id && topicId !== selectedTopic?.externalId) {
      loadTopic(topicId)
    }
  }, [topicId, loadTopic, selectedTopic])

  useEffect(() => {
    if (requestNextTopicId) {
      navigate(`${routes.moderation}/${requestNextTopicId}`)
    }
  }, [requestNextTopicId, navigate])

  const onSelectTopic = useCallback(
    (topicId?: string) => {
      navigate(`${routes.moderation}/${topicId}`)
    },
    [navigate]
  )

  return (
    <Navigation currentRoute="moderation">
      <RoleGuard allowedRoles={['moderator', 'operator', 'commentator']}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-around',
            alignItems: 'stretch',
            flex: 1
          }}
        >
          {!isMobile && (
            <TopicList
              topics={topics}
              topicFilter={topicFilter}
              setTopicFilter={setTopicFilter}
              selectedTopic={selectedTopic?.id}
              setSelectedTopic={onSelectTopic}
              openAreenaTopicModal={
                isOperator(user) ? () => setIsAreenaTopicModalOpen(true) : undefined
              }
            />
          )}
          {selectedTopic ? (
            <CommentList
              topic={{ ...selectedTopic, subscriptionPeriod: period }}
              comments={comments}
              notices={notices}
              actions={{ ...actions, onSubscribeTopicEmails }}
              tab={tab}
              setTab={setTab}
              loading={loading}
              editTopic={
                isOperator(user)
                  ? (topic: Topic) => {
                      setEditedAreenaTopic(topic)
                      setIsAreenaTopicModalOpen(true)
                    }
                  : undefined
              }
            />
          ) : (
            <Box sx={{ flex: 1.6 }} />
          )}
          <AreenaTopicModal
            open={isAreenaTopicModalOpen}
            topic={editedAreenaTopic}
            handleClose={() => {
              setEditedAreenaTopic(undefined)
              setIsAreenaTopicModalOpen(false)
            }}
            navigate={navigate}
          />
        </Box>
      </RoleGuard>
    </Navigation>
  )
}
